<template>
  <BaseSection :is-main-heading="true" heading="TKD Serviceleistungen">
    <BaseSection heading="SRT & Batterieeinheit Bundle ">
      <div class="md:flex md:gap-4">
        <BaseDropdown
          v-model:selection="deviceService.type"
          :validation="v$.deviceService.type"
          test-id="service-type-possible"
          label="Serviceleistung wählen"
          dropdown-text="Serviceleistung wählen"
          :options-list="deviceServiceList"
          :disabled="isOldDeviceMissing"
          class="md:basis-3/4"
        />
        <DateMount
          class="md:basis-1/4"
          :device-event="recordingDate"
          label="Servicedatum"
          disabled
          test-id="recordingDate"
        />
      </div>

      <BaseDropdown
        v-if="!v$.deviceService.reason.required?.$params?.optional"
        v-model:selection="deviceService.reason"
        label="Grund auswählen"
        test-id="service-reason"
        dropdown-text="Grund auswählen"
        :options-list="deviceServiceReasons"
        class="md:basis-3/4"
        :validation="v$.deviceService.reason"
        @update:selection="() => (deviceService.note = undefined)"
      />

      <BaseTextarea
        v-if="showNoteField"
        v-model:input-text="deviceService.note"
        label="Bemerkung"
        test-id="repair_note"
        :validation="v$.deviceService.note"
      />

      <Checkbox
        v-if="
          deviceService.type === DeviceServiceTypeEnum.REMAIN ||
          deviceService.type === DeviceServiceTypeEnum.REPAIR
        "
        v-model:checked="isDeviceTested"
        label="Funktion und Leistung geprüft"
        checkbox-id="checkedDevice"
      />

      <BaseSection
        v-if="
          (deviceService.type === DeviceServiceTypeEnum.REPAIR ||
            isExchangeService) &&
          !showCounterPressureSection
        "
        heading="Ventilgegendruck"
      >
        <div class="flex gap-4">
          <BaseFormInput
            v-model.number="currentDevice.counterPressure"
            :validation="v$.device.counterPressure"
            label="Ventilgegendruck"
            inputmode="decimal"
            type="number"
            test-id="counterPressure"
            class="grow"
          />
          <MeasurementUnit
            :device="currentDevice"
            :unit="device.measurementUnit"
          />
        </div>
      </BaseSection>
    </BaseSection>
  </BaseSection>

  <BaseSection v-if="isExchangeService" heading="SRT-Daten">
    <RecordDeviceDetails
      v-model:device="device"
      :validation="v$.device"
      :show-counter-pressure="false"
    />
  </BaseSection>

  <BaseSection v-if="isExchangeService" heading="Batterieeinheit">
    <BatteryPackDetails v-model="batteryPack" :validation="v$.batteryPack" />
  </BaseSection>

  <BaseSection
    heading="Adapter"
    :required="false"
    :label="amountOfAdapters.toString()"
  >
    <AccessoryManager
      ref="accessoryComponentRef"
      v-model="installationPoint"
      v-model:accessory-service-list="accessoryServiceList"
      v-model:device-service="deviceService"
      @update-amount-of-adapters="(val: Adapter) => (amountOfAdapters = val)"
      @update-adapter-change="resetCounterOnPressure"
    />
  </BaseSection>
  <BaseSection
    v-if="showCounterPressureSection"
    heading="Ventilkraft nach Adapteranpassung"
    :required="true"
  >
    <BaseInformation
      class="-mb-px mt-4"
      status="warning"
      information-text="Bitte messen Sie den Ventilgegendruck erst nachdem alle Adapter installiert wurden."
    />
    <div class="flex gap-4">
      <BaseFormInput
        v-if="isExchangeService"
        v-model.number="device.counterPressure"
        :validation="v$.device.counterPressure"
        label="Ventilgegendruck"
        inputmode="decimal"
        type="number"
        test-id="counterPressure"
        class="grow"
      />
      <BaseFormInput
        v-else
        v-model.number="currentDevice.counterPressure"
        :validation="v$.device.counterPressure"
        label="Ventilgegendruck"
        inputmode="decimal"
        type="number"
        test-id="counterPressure"
        class="grow"
      />
      <MeasurementUnit :device="device" :unit="device.measurementUnit" />
    </div>
  </BaseSection>
</template>
<script setup lang="ts">
import BaseSection from '@/components/UI/Card/BaseSection.vue';
import BaseDropdown from '@/components/UI/Dropdown/BaseDropdown.vue';
import DateMount from '@/components/Forms/DeviceFormComponents/DateMount.vue';
import { computed, ref, watch } from 'vue';
import BaseTextarea from '@/components/UI/Note/BaseTextarea.vue';
import Checkbox from '@/components/UI/Checkbox/Checkbox.vue';
import BaseFormInput from '@/components/UI/Input/BaseFormInput.vue';
import MeasurementUnit from '@/components/Forms/DeviceFormComponents/MeasurementUnit.vue';
import {
  adapterDemountReasonList,
  AdapterServiceTypeEnum,
  DeviceServiceReasonEnum,
  deviceServiceReasonList,
  DeviceServiceTypeEnum,
  deviceServiceList,
  deviceServiceRemainReasonList,
  missingOldDeviceReasonList,
  noVerificationPossibleReasonList,
} from '@/enums/device';
import { InstallationPoint } from '@/models/installationPoint/InstallationPoint';
import BatteryPackDetails from '@/components/Forms/DeviceFormComponents/BatteryPackDetails.vue';
import RecordDeviceDetails from '@/components/Forms/DeviceFormComponents/RecordDeviceDetails.vue';
import { useValidation } from '@/composables/useValidation';
import { Device } from '@/models/devices/Device';
import { Adapter, BatteryPack } from '@/models/installationPoint/Accessory';
import { DeviceService } from '@/models/installationPoint/DeviceService';
import { AccessoryService } from '@/models/installationPoint/AccessoryService';
import BaseInformation from '@/components/UI/Hint/BaseInformation.vue';
import AccessoryManager from '@/components/Forms/DeviceFormComponents/ExchangeAdapters/AccessoryManager.vue';

const installationPoint = defineModel<InstallationPoint>('installationPoint');
const deviceService = defineModel<DeviceService>('deviceService');
const currentDevice = defineModel<Device<any>>('currentDevice');
const device = defineModel<Device<any>>('newDevice');
const accessoryServiceList = defineModel<Record<string, AccessoryService>>(
  'accessoryServiceList'
);
const batteryPack = defineModel<BatteryPack>('newBatteryPack');

const { v$, isFormValid } = useValidation({
  device,
  batteryPack,
  deviceService,
});

const emit = defineEmits(['update:modelValue']);
const isDeviceTested = ref(false);
const counterPressureChanged = ref(false);
const amountOfAdapters = ref(0);

const isOldDeviceMissing = computed(() => {
  return (
    !!deviceService.value?.deviceVerificationStatus
      ?.isVerificationNotPossible &&
    deviceService.value?.deviceVerificationStatus?.reason ===
      noVerificationPossibleReasonList[0].value
  );
});

const deviceServiceReasons = computed(() => {
  if (isOldDeviceMissing.value) {
    return missingOldDeviceReasonList;
  }
  if (deviceService.value?.type === DeviceServiceTypeEnum.REMAIN) {
    return deviceServiceRemainReasonList;
  }
  if (deviceService.value?.type === DeviceServiceTypeEnum.EXCHANGE) {
    return deviceServiceReasonList;
  }
  return [];
});

const recordingDate = computed(() => {
  return {
    timestamp: new Date().toISOString(),
  };
});

const showNoteField = computed(() => {
  return deviceService.value?.reason === DeviceServiceReasonEnum.OTHER;
});

const showCounterPressureSection = computed(() => {
  return (
    counterPressureChanged.value &&
    deviceService.value?.type !== DeviceServiceTypeEnum.UNMOUNT
  );
});

const isExchangeService = computed(() => {
  return deviceService.value?.type === DeviceServiceTypeEnum.EXCHANGE;
});

const shouldSyncCounterPressure = computed(() => {
  return isExchangeService.value && !showCounterPressureSection.value;
});

const accessoryComponentRef = ref(null);

const isLocalFormValid = computed(() => {
  const accessoryValid = accessoryComponentRef.value?.isFormValid;

  if (!accessoryValid) return false;

  if (deviceService.value?.type === DeviceServiceTypeEnum.UNMOUNT) return true;

  if (
    counterPressureChanged.value &&
    !currentDevice.value?.counterPressure &&
    !device.value?.counterPressure
  ) {
    return false;
  }

  if (deviceService.value?.type === DeviceServiceTypeEnum.REMAIN) {
    if (deviceService.value.reason === DeviceServiceReasonEnum.OTHER) {
      return (
        isDeviceTested.value &&
        deviceService.value.reason &&
        deviceService.value.note
      );
    }
    return isDeviceTested.value && deviceService.value.reason;
  }

  if (isExchangeService.value) {
    return isFormValid.value;
  }

  if (deviceService.value?.type === DeviceServiceTypeEnum.REPAIR) {
    // For REPAIR, counter pressure is always required
    const hasCounterPressure = showCounterPressureSection.value
      ? device.value?.counterPressure || currentDevice.value?.counterPressure
      : currentDevice.value?.counterPressure;

    return (
      isDeviceTested.value &&
      !!hasCounterPressure &&
      !v$.value.deviceService.$invalid
    );
  }

  return !v$.value.deviceService.$invalid;
});

watch(isLocalFormValid, (val) => {
  emit('update:modelValue', val);
});

const resetCounterOnPressure = () => {
  currentDevice.value.counterPressure = undefined;
  device.value.counterPressure = undefined;
  counterPressureChanged.value = true;
};

watch(
  () => currentDevice.value?.id,
  () => {
    if (currentDevice.value && 'counterPressure' in currentDevice.value) {
      currentDevice.value.counterPressure = undefined;
    }
  },
  { immediate: true }
);

watch(
  isOldDeviceMissing,
  (isMissing) => {
    if (!deviceService.value || !isMissing) return;
    deviceService.value.type = DeviceServiceTypeEnum.EXCHANGE;
  },
  { immediate: true }
);

watch(
  () => [shouldSyncCounterPressure.value, currentDevice.value?.counterPressure],
  ([shouldSync]) => {
    if (shouldSync && device.value) {
      device.value.counterPressure = currentDevice.value?.counterPressure;
    }
  },
  { immediate: true }
);

watch(
  () => deviceService.value?.type,
  (newType, oldType) => {
    if (newType !== oldType && deviceService.value) {
      deviceService.value.reason = undefined;
      deviceService.value.note = undefined;
    }

    if (newType === DeviceServiceTypeEnum.UNMOUNT) {
      Object.values(accessoryServiceList.value).forEach(
        (service: AccessoryService) => {
          service.type = AdapterServiceTypeEnum.DEMOUNT;
          service.reason = adapterDemountReasonList[1].value;
        }
      );
      return;
    }
    Object.values(accessoryServiceList.value).forEach(
      (service: AccessoryService) => {
        service.type = undefined;
        service.reason = undefined;
      }
    );
    counterPressureChanged.value = false;
  }
);
</script>
